
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SuperAdminService } from '../../../services/super-admin/super-admin.service';
import { PaymentService as ReportsService } from '../../../services/payments/payment/reports.service';
import { PaymentService } from '../../../services/payments/payment/payment.service';
import { PaymentReportRequest, PaymentReportResponse, PaymentTransactionFromAPI } from '../../../models/paymentreport.model';
import { PaymentTransaction, PaymentStatus, PaymentReportSummary } from '../../../models/payment/paymentReport.model';
import { Store } from '@ngxs/store';
import { UserSelectors } from '../../../states/user/user-selector';
import { AuthRoles } from '../../../models/enums/enums';
import { PackagesService } from '../../../services/payments/packages/packages.service';
import { Package } from '../../../models/payment/package.model';
import { RefundRequest } from '../../../models/payment/refund.model';
import { PaymentStatusRequest } from '../../../models/payment/paymentStatus.model';
import { HttpResponse } from '@angular/common/http';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-payment-reports',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    CalendarModule,
    TableModule,
    DialogModule,
    ToastModule,
    TooltipModule,
    ProgressSpinnerModule
  ],
  providers: [MessageService],
  templateUrl: './payment-reports.component.html',
  styleUrl: './payment-reports.component.scss'
})
export class PaymentReportsComponent implements OnInit {
  filterForm!: FormGroup;
  refundForm!: FormGroup;
  transactions: PaymentTransaction[] = [];
  summary: PaymentReportSummary = {
    totalTransactions: 0,
    totalAmount: 0,
    successfulPayments: 0,
    successAmount: 0
  };
  isLoading: boolean = false;
  isExportLoading: boolean = false;
  isRefundProcessing: boolean = false;
  showFilters: boolean = false;
  showRefundDialog: boolean = false;
  selectedTransaction: PaymentTransaction | null = null;
  packages: Package[] = [];
  isSuperAdmin: boolean = false;
  isAdmin: boolean = false;

  statusOptions = [
    { label: 'All Status', value: null },
    { label: 'Success', value: 1 },
    { label: 'Pending', value: 0 },
    { label: 'Failed', value: 2 },
    { label: 'Refunded', value: 3 }
  ];

  paymentMethodOptions = [
    { label: 'All Methods', value: null },
    { label: 'UPI', value: 'UPI' },
    { label: 'Card', value: 'Card' },
    { label: 'Net Banking', value: 'Net Banking' }
  ];


  constructor(
    private formBuilder: FormBuilder,
    private superAdminService: SuperAdminService,
    private reportsService: ReportsService,
    private paymentService: PaymentService,
    private packagesService: PackagesService,
    private messageService: MessageService,
    private store: Store
  ) { }

  ngOnInit() {
    this.subscribeToUserRole();
    this.initializeForm();
    this.initializeRefundForm();
    this.loadPackages();
    this.loadPaymentReports();
  }

  subscribeToUserRole() {
    this.store.select(UserSelectors.getUser).subscribe({
      next: (user) => {
        if (user?.role) {
          // Check for role using both number and string check for robustness
          this.isSuperAdmin = user.role.some(r => String(r) == String(AuthRoles.SuperAdmin) || r == AuthRoles.SuperAdmin);
          this.isAdmin = user.role.some(r => String(r) == String(AuthRoles.Admin) || r == AuthRoles.Admin) && !this.isSuperAdmin;
        }
      }
    });
  }

  initializeForm() {
    this.filterForm = this.formBuilder.group({
      searchText: [''],
      packageId: [null],
      status: [null],
      paymentMethod: [null],
      dateRange: [null]
    });
  }

  initializeRefundForm() {
    this.refundForm = this.formBuilder.group({
      amount: [0, [Validators.required, Validators.min(1)]]
    });
  }

  loadPackages() {
    this.packagesService.getAllPackages({
      name: '',
      servicesIncluded: [''],
      duration: 0,
      amount: 0,
      description: ''
    }).subscribe({
      next: (data) => {
        const arr = Array.isArray(data) ? data : (data as any)?.result || (data as any)?.value || (data as any)?.data || [];
        this.packages = arr.map((item: any) => ({
          id: item.Id ?? item.id ?? 0,
          name: item.Name ?? item.name ?? '',
          description: item.Description ?? item.description ?? '',
          status: item.Status ?? item.status ?? 'Active',
          duration: item.Duration ?? item.duration ?? 0,
          servicesIncluded: item.ServicesIncluded ?? item.servicesIncluded ?? [],
          amount: item.Amount ?? item.amount ?? 0,
          currency: item.Currency ?? item.currency ?? '₹',
          isReadOnly: item.IsReadOnly ?? item.isReadOnly ?? false
        }));
      },
      error: (err: any) => {
        console.error('Error loading packages:', err);
      }
    });
  }

  loadPaymentReports() {
    this.isLoading = true;
    const request = this.buildRequest();

    this.reportsService.paymentReport(request).subscribe({
      next: (response: PaymentReportResponse) => {
        const list = response?.result ?? [];
        this.transactions = list.map((item) => this.mapTransactionFromAPI(item));
        this.summary = {
          totalTransactions: response?.total ?? 0,
          totalAmount: this.transactions.reduce((acc, curr) => acc + curr.amount, 0),
          successfulPayments: this.transactions.filter(t => t.status === PaymentStatus.Success).length,
          successAmount: this.transactions.filter(t => t.status === PaymentStatus.Success).reduce((acc, curr) => acc + curr.amount, 0)
        };
        this.isLoading = false;
      },
      error: (err: any) => {
        console.error('Error loading payment reports:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load payment reports'
        });
        this.isLoading = false;
      }
    });
  }

  private mapStatus(status: number): PaymentStatus {
    switch (status) {
      case 0: return PaymentStatus.InProgress;
      case 1: return PaymentStatus.Success;
      case 2: return PaymentStatus.Failed;
      case 3: return PaymentStatus.Refunded;
      default: return PaymentStatus.InProgress;
    }
  }

  private mapTransactionFromAPI(item: PaymentTransactionFromAPI): PaymentTransaction {
    return {
      orderId: item.transactionNumber,
      transactionId: item.transactionNumber,
      customerName: item.customerName,
      customerEmail: item.email || '',
      packageName: item.packageName,
      amount: item.amount,
      discountAmount: undefined,
      paymentMethod: item.paymentMode,
      status: this.mapStatus(item.status),
      date: item.attemptedAt,
      phoneNumber: item.phoneNumber || ''
    };
  }

  buildRequest(): PaymentReportRequest {
    const formValue = this.filterForm.value;
    const dateRange = formValue.dateRange as Date[] | null;

    return {
      name: formValue.searchText || null,
      packageId: formValue.packageId || null,
      paymentStatus: formValue.status !== null ? Number(formValue.status) : null,
      paymentMode: formValue.paymentMethod || null,
      dateFrom: dateRange && dateRange[0] ? this.formatDate(dateRange[0]) : null,
      dateTo: dateRange && dateRange[1] ? this.formatDate(dateRange[1]) : null
    };
  }

  formatDate(date: Date): string | null {
    if (!date) return null;
    try {
      return new Date(date).toISOString();
    } catch (e) {
      return null;
    }
  }

  onFilter() {
    this.loadPaymentReports();
  }

  clearAllFilters() {
    this.filterForm.reset({
      searchText: '',
      packageId: null,
      status: null,
      paymentMethod: null,
      dateRange: null
    });
    this.loadPaymentReports();
  }

  toggleFilters() {
    this.showFilters = !this.showFilters;
  }

  exportReport() {
    this.isExportLoading = true;
    const request = this.buildRequest();

    this.superAdminService.exportPaymentReports(request as any).subscribe({
      next: (response: HttpResponse<ArrayBuffer>) => {
        const cd = response.headers.get('Content-Disposition');
        const filename = this.getFilenameFromContentDisposition(cd) ||
          `Payment_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
        const blob = new Blob([response.body!], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        this.saveBlobAsFile(blob, filename);

        this.isExportLoading = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Report exported successfully'
        });
      },
      error: (err: any) => {
        console.error('Export failed:', err);
        this.isExportLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to export report'
        });
      }
    });
  }

  private getFilenameFromContentDisposition(contentDisposition: string | null): string | null {
    if (!contentDisposition) return null;
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      return filenameMatch[1].replace(/['"]/g, '');
    }
    return null;
  }

  private saveBlobAsFile(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  getStatusClass(status: PaymentStatus): string {
    switch (status) {
      case PaymentStatus.Success:
        return 'status-success';
      case PaymentStatus.InProgress:
        return 'status-pending';
      case PaymentStatus.Failed:
        return 'status-failed';
      case PaymentStatus.Refunded:
        return 'status-refunded';
      default:
        return '';
    }
  }

  formatCurrency(amount: number): string {
    return `₹${amount.toLocaleString('en-IN')}`;
  }

  formatDateDisplay(date: string): string {
    if (!date) return '';
    const d = new Date(date);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  }

  onRefund(transaction: PaymentTransaction) {
    this.selectedTransaction = transaction;
    this.refundForm.patchValue({
      amount: transaction.amount
    });
    // Set max validator dynamically
    this.refundForm.get('amount')?.setValidators([
      Validators.required,
      Validators.min(1),
      Validators.max(transaction.amount)
    ]);
    this.refundForm.get('amount')?.updateValueAndValidity();
    this.showRefundDialog = true;
  }

  closeRefundDialog() {
    this.showRefundDialog = false;
    this.selectedTransaction = null;
    this.refundForm.reset();
  }

  processRefund() {
    if (this.refundForm.invalid || !this.selectedTransaction) {
      return;
    }

    this.isRefundProcessing = true;
    const refundRequest: RefundRequest = {
      originalTxnNo: this.selectedTransaction.transactionId,
      transactionType: 'REFUND',
      amount: this.refundForm.value.amount
    };

    this.paymentService.refundTransaction(refundRequest).subscribe({
      next: (response) => {
        this.isRefundProcessing = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Refund Successful',
          detail: `${response.respDescription ?? (response as any).RespDescription ?? 'Transaction refunded successfully'}`,
          life: 5000
        });
        this.closeRefundDialog();
        this.loadPaymentReports();
      },
      error: (err) => {
        this.isRefundProcessing = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Refund Failed',
          detail: err?.error?.message || 'Failed to process refund. Please try again.',
          life: 5000
        });
      }
    });
  }

  onUpdateStatus(transaction: PaymentTransaction) {
    const request: PaymentStatusRequest = {
      merchantTxnNo: transaction.transactionId,
      originalTxnNo: transaction.transactionId,
      transactionType: 'STATUS'
    };

    this.paymentService.updatePaymentStatus(request).subscribe({
      next: (response: string) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Status Updated',
          detail: response || 'Payment status updated successfully',
          life: 5000
        });
        this.loadPaymentReports();
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.message || 'Failed to update status',
          life: 5000
        });
      }
    });
  }
}


